/**
 * Old-dialect blank lines (markdown-editor-parity): the old editor saved an empty paragraph as
 * `<br>` on its own line. RichMarkdownInput reads one as a paragraph break and drops a block that
 * is only `<br>` (L2), and this plugin does the same when rendering, so an old body shows one
 * block gap between paragraphs, as a new body does, and not a `<br>` line plus the gap.
 *
 * Runs before remark-breaks, which turns the newlines it looks for into `break` nodes.
 */

export const BR_ONLY = /^\s*(<br\s*\/?>\s*)+$/i

/**
 * An old-dialect blank line among a paragraph's children: a `<br>` with a newline (or the
 * paragraph edge) on both sides.
 */
export const isOldBlankLine = (children: any[], i: number) => {
  const prev = children[i - 1]
  const next = children[i + 1]
  return (
    children[i].type === 'html' &&
    BR_ONLY.test(children[i].value) &&
    (!prev || (prev.type === 'text' && prev.value.endsWith('\n'))) &&
    (!next || (next.type === 'text' && next.value.startsWith('\n')))
  )
}

const FLOW_PARENTS = ['root', 'blockquote', 'listItem']

const trimEdgeNewlines = (segment: any[]) =>
  segment.map((node, i) => {
    if (node.type !== 'text') {
      return node
    }
    let value = node.value
    if (i === 0) {
      value = value.replace(/^\n+/, '')
    }
    if (i === segment.length - 1) {
      value = value.replace(/\n+$/, '')
    }
    return value === node.value ? node : { ...node, value }
  })

const isBlank = (segment: any[]) => segment.every(node => node.type === 'text' && node.value.trim() === '')

const splitParagraph = (paragraph: any) => {
  const segments: any[][] = [[]]
  paragraph.children.forEach((child, i) => {
    if (isOldBlankLine(paragraph.children, i)) {
      segments.push([])
    } else {
      segments[segments.length - 1].push(child)
    }
  })
  if (segments.length === 1) {
    return [paragraph]
  }
  return segments
    .map(trimEdgeNewlines)
    .filter(segment => !isBlank(segment))
    .map(children => ({ type: 'paragraph', children }))
}

export function remarkOldBlankLines() {
  const walk = (parent: any) => {
    if (!parent.children) {
      return
    }
    parent.children = parent.children.flatMap(child => {
      if (child.type === 'paragraph') {
        return splitParagraph(child)
      }
      if (child.type === 'html' && FLOW_PARENTS.includes(parent.type) && BR_ONLY.test(child.value)) {
        return []
      }
      walk(child)
      return [child]
    })
  }
  return walk
}
